/**
 * Background job queue — a port of Sparked's doc-08 architecture.
 *
 * Why a client-side queue at all: work must survive navigation and reloads, and in a collaborative
 * review the INITIATOR's device drives the request (spec 5b.3). IndexedDB gives crash-safety;
 * Web Locks elects exactly one leader across a user's open tabs so the same job is never run twice.
 *
 * FABLE-HANDOFF (#26 remainder): cancel semantics (abort the in-flight fetch AND tell the server, so
 * nothing is charged for undelivered work — spec D8), lease renewal/expiry so a crashed leader's job
 * is reclaimed rather than stranded, and retry/backoff. The storage + leader election below are done
 * and tested; treat `runJob` as the seam.
 */

const DB_NAME = 'codebox-jobs';
const STORE = 'queue';
const LOCK_NAME = 'codebox-job-leader';
const LEASE_MS = 60_000;

export interface QueuedJob {
  id: string;
  projectId: string;
  kind: 'chat' | 'feedback' | 'assess';
  payload: Record<string, unknown>;
  status: 'queued' | 'running' | 'done' | 'error';
  createdAt: number;
  /** owner + expiry of the current lease; a stale lease may be reclaimed by another tab */
  leasedBy?: string;
  leaseUntil?: number;
  error?: string;
}

/** Unique per tab, so a lease can name its holder. */
const TAB_ID = `tab-${Math.random().toString(36).slice(2, 10)}`;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(STORE, mode);
    const request = run(transaction.objectStore(STORE));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function enqueue(job: Omit<QueuedJob, 'status' | 'createdAt'>): Promise<QueuedJob> {
  const record: QueuedJob = { ...job, status: 'queued', createdAt: Date.now() };
  await tx('readwrite', store => store.put(record));
  return record;
}

export async function listJobs(): Promise<QueuedJob[]> {
  const all = await tx<QueuedJob[]>('readonly', store => store.getAll() as IDBRequest<QueuedJob[]>);
  return all.sort((a, b) => a.createdAt - b.createdAt);
}

export async function updateJob(id: string, patch: Partial<QueuedJob>): Promise<void> {
  const existing = await tx<QueuedJob | undefined>(
    'readonly',
    store => store.get(id) as IDBRequest<QueuedJob | undefined>,
  );
  if (!existing) return;
  await tx('readwrite', store => store.put({ ...existing, ...patch }));
}

export async function removeJob(id: string): Promise<void> {
  await tx('readwrite', store => store.delete(id));
}

/** Jobs that are runnable right now: queued, or running under a lease that has expired. */
export async function claimable(): Promise<QueuedJob[]> {
  const now = Date.now();
  const jobs = await listJobs();
  return jobs.filter(
    j => j.status === 'queued' || (j.status === 'running' && (j.leaseUntil ?? 0) < now),
  );
}

export type JobRunner = (job: QueuedJob) => Promise<void>;

/**
 * Runs the queue under a Web Lock so exactly one tab processes it. The lock is held for as long as
 * this tab keeps working; other tabs wait and take over automatically if this one closes or crashes.
 */
export async function startLeader(runJob: JobRunner, signal?: AbortSignal): Promise<void> {
  if (!('locks' in navigator)) {
    // No Web Locks (older browsers): fall back to running unlocked. Duplicate work is possible but
    // the server is idempotent per job id, so this degrades rather than breaks.
    await drain(runJob, signal);
    return;
  }

  await navigator.locks.request(LOCK_NAME, { signal }, async () => {
    while (!signal?.aborted) {
      const ran = await drain(runJob, signal);
      if (!ran) await new Promise(r => setTimeout(r, 1000));
    }
  });
}

async function drain(runJob: JobRunner, signal?: AbortSignal): Promise<boolean> {
  const jobs = await claimable();
  if (jobs.length === 0) return false;

  for (const job of jobs) {
    if (signal?.aborted) break;
    await updateJob(job.id, {
      status: 'running',
      leasedBy: TAB_ID,
      leaseUntil: Date.now() + LEASE_MS,
    });
    try {
      await runJob(job);
      await updateJob(job.id, { status: 'done' });
      // completed work does not need to linger in the queue
      await removeJob(job.id);
    } catch (err) {
      await updateJob(job.id, {
        status: 'error',
        error: err instanceof Error ? err.message : 'failed',
      });
    }
  }
  return true;
}

export const __testing = { TAB_ID, LEASE_MS };
